import { useCallback, useState } from "react";

/**
 * A boolean view toggle (checkbox / show-hide button) that survives a remount.
 *
 * Maps and panels here live inside tabs: switching to another tab and back unmounts the
 * component, which would silently reset every toggle the user had clicked. Anything that only
 * controls what is DRAWN (overlays, labels, raw preview) should therefore be persisted per
 * browser — it is a preference, not part of the record.
 *
 * Not for state that changes what the user is about to DO (add-mode, edit-mode, armed tools):
 * restoring those on entry would put the map in a mode nobody asked for.
 */
export function usePersistentToggle(
  key: string,
  initial: boolean,
): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : raw === "1";
    } catch {
      // Storage unavailable (private mode) — fall back to the default.
      return initial;
    }
  });
  const set = useCallback(
    (next: boolean) => {
      setValue(next);
      try {
        localStorage.setItem(key, next ? "1" : "0");
      } catch {
        // Ignore write failures (quota / private mode) — persistence is best-effort.
      }
    },
    [key],
  );
  return [value, set];
}
